import React, { useState, useEffect } from 'react';
import { companiesService } from '../services/api';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { loadActivityMode } from '../utils/activityModeStorage';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    companyType: 'B2B',
    name: '',
    address: '',
    postalCode: '',
    city: '',
    country: 'France',
    phone: '',
    email: '',
    siret: '',
    vatNumber: '',
    notes: ''
  });

  // Fonction pour charger les entreprises depuis l'API MySQL
  const loadCompanies = async () => {
    try {
      const data = await companiesService.getAll();
      setCompanies(data);
    } catch (error) {
      console.error('Erreur lors du chargement des entreprises:', error);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const handleOpenDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        companyType: company.companyType || 'B2B',
        name: company.name || '',
        address: company.address || '',
        postalCode: company.postalCode || '',
        city: company.city || '',
        country: company.country || 'France',
        phone: company.phone || '',
        email: company.email || '',
        siret: company.siret || '',
        vatNumber: company.vatNumber || '',
        notes: company.notes || ''
      });
    } else {
      setEditingCompany(null);
      const activity = loadActivityMode();
      const defaultCompanyType = activity?.id === 'beauty' ? 'B2C' : 'B2B';
      setFormData({
        companyType: defaultCompanyType,
        name: '',
        address: '',
        postalCode: '',
        city: '',
        country: 'France',
        phone: '',
        email: '',
        siret: '',
        vatNumber: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCompany(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        siret: formData.companyType === 'B2C' ? '' : formData.siret,
        vatNumber: formData.companyType === 'B2C' ? '' : formData.vatNumber,
      };

      if (editingCompany) {
        // Mise à jour d'une entreprise existante
        await companiesService.update(editingCompany.id, payload);
      } else {
        // Ajout d'une nouvelle entreprise
        await companiesService.create(payload);
      }
      await loadCompanies();
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Gérer les erreurs spécifiques de doublons (code 409)
      if (error.response?.status === 409) {
        const errorData = error.response.data;
        alert(errorData.message || errorData.error || 'Ce client existe déjà dans votre compte.');
      } else if (error.response?.status === 403) {
        // Erreur de limitation d'abonnement
        const errorData = error.response.data;
        alert(errorData.message || 'Vous avez atteint la limite de votre plan. Passez au plan PRO pour ajouter plus de clients.');
      } else {
        alert('Erreur lors de la sauvegarde du client. Veuillez réessayer.');
      }
    }
  };

  const handleDelete = async (companyId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        await companiesService.delete(companyId);
        await loadCompanies();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Gestion des Clients
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: 'primary.main' }}
        >
          Nouveau Client
        </Button>
      </Box>

      {/* Section de recherche */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Rechercher des clients
          </Typography>
          <TextField
            fullWidth
            placeholder="Rechercher par nom, email, téléphone, SIRET..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {companies.filter(company => 
              company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              company.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              company.phone?.includes(searchTerm) ||
              company.siret?.includes(searchTerm)
            ).length} client(s) trouvé(s) sur {companies.length} total
          </Typography>
        </CardContent>
      </Card>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Clients
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {companies.length}
                  </Typography>
                </Box>
                <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Clients Actifs
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {companies.length}
                  </Typography>
                </Box>
                <Chip label="100%" color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Liste des entreprises */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Liste des Clients
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Nom</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Adresse</strong></TableCell>
                  <TableCell><strong>Contact</strong></TableCell>
                  <TableCell><strong>SIRET</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.filter(company => 
                  company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  company.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  company.phone?.includes(searchTerm) ||
                  company.siret?.includes(searchTerm)
                ).map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {company.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          TVA: {company.vatNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={company.companyType === 'B2C' ? 'Particulier' : 'Entreprise'}
                        color={company.companyType === 'B2C' ? 'default' : 'primary'}
                        variant={company.companyType === 'B2C' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2">{company.address}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {company.postalCode} {company.city}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {company.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2">{company.phone}</Typography>
                          </Box>
                        )}
                        {company.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2">{company.email}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {company.siret || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(company)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(company.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog pour ajouter/éditer une entreprise */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCompany ? 'Modifier le Client' : 'Nouveau Client'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type de client</InputLabel>
                <Select
                  label="Type de client"
                  name="companyType"
                  value={formData.companyType || 'B2B'}
                  onChange={handleInputChange}
                >
                  <MenuItem value="B2B">Entreprise</MenuItem>
                  <MenuItem value="B2C">Particulier</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={formData.companyType === 'B2C' ? 'Nom du particulier' : 'Nom de l\'entreprise'}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            {formData.companyType !== 'B2C' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SIRET"
                  name="siret"
                  value={formData.siret}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Code Postal"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Ville"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pays"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            {formData.companyType !== 'B2C' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Numéro de TVA"
                  name="vatNumber"
                  value={formData.vatNumber}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCompany ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Companies;